'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import type { BalanceSheetFlatRow } from '@/new_components/balance_sheet/balanceSheetService';
import { CollapsibleCard } from '@/new_components/guv/CollapsibleCard';
import { FlatAktivaFinancialTable } from '@/new_components/guv/FlatAktivaFinancialTable';
import type { FlatFinancialRow } from '@/new_components/guv/FlatFinancialTable';
import { FlatFinancialTable } from '@/new_components/guv/FlatFinancialTable';
import { FlatPassivaFinancialTable } from '@/new_components/guv/FlatPassivaFinancialTable';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import RawFinancialsCard from '@/new_components/raw_financials/RawFinancialsCard';
import { useTranslation } from 'react-i18next';
import { trackCompanyView } from '@/lib/analytics';
import type {
  CompanyBasicResponse,
  CompanyContactResponse,
  CompanyFinancialsResponse,
  CompanyGesellschafterResponse,
  CompanyHistory,
  CompanyLegalStatus,
  CompanyNetworkResponse,
  CompanyRegisterResponse,
} from '@/lib/backend-api';
import { companyCountryCode } from '@/lib/company-utils';
import { FreezeReporter } from '@/lib/freeze-reporter';
import { delocalizePath } from '@/lib/i18n-routes';
import { isAdmin, isPro } from '@/lib/permissions';
import { useRecordCompanyView } from '@/hooks/use-recent-activity';
import { useSession } from '@/hooks/use-session';
import { Container } from '@/components/common/container';
import {
  backfillAiSummaryAction,
  triggerAiSummaryForGuestIfFirstVisit,
} from '@/app/actions/backfill-actions';
import { ChartTableTabs } from './ChartTableTabs';
import { CompanyAdminActionsCard } from './CompanyAdminActionsCard';
import {
  DOWNLOADABLE_FILE_TYPES,
  buildCompanyProfileHeroData,
  buildCompanyProfileStatistics,
  getEffectiveCompanyRegisterData,
  getEffectiveCompanyFinancialsData,
  formatRegisterNumber,
} from './companyDetailUtils';
import { CompanyHistoryCard } from './CompanyHistoryCard';
import { CompanyPeerSearchCard } from './CompanyPeerSearchCard';
import { CompanyProfileSection } from './CompanyProfileSection';
import { CompanyValidationErrorsCard } from './CompanyValidationErrorsCard';
import { ContactInformationCard } from './ContactInformationCard';
import {
  extractCostsForYear,
  getGkvCategories,
  getUkvCategories,
  isUkvStyle,
} from './costCategories';
import { CostsBreakdownTable } from './CostsBreakdownTable';
import { createFormatters } from './currencyFormatters';
import { FinancialDocsCard } from './FinancialDocsCard';
import { FinancialHistoryTable } from './FinancialHistoryTable';
import { FundingCard, FundingCardPlaceholder } from './FundingCard';
import { MobileActionBar } from './MobileActionBar';
import { PeopleTabbedCard } from './PeopleTabbedCard';
import { RegisterDocsCard } from './RegisterDocsCard';
import { SwissDataSourceNote } from './SwissDataSourceNote';
import { ZeitverlaufTabbedCard } from './ZeitverlaufTabbedCard';

// ---------------------------------------------------------------------------
// Lazy-loaded heavy chart components (recharts / XyFlow) — defer until visible.
// Loading skeletons match the final rendered heights to avoid CLS.
// ---------------------------------------------------------------------------
const ChartSkeleton = () => <div className="h-[315px]" />;
const CostsSkeleton = () => <div className="h-[420px]" />;

const CompanyTrendChart = dynamic(
  () => import('./CompanyTrendChart').then((mod) => mod.CompanyTrendChart),
  { ssr: false, loading: ChartSkeleton },
);

const CompanyCostsPieChart = dynamic(
  () =>
    import('./CompanyCostsPieChart').then((mod) => mod.CompanyCostsPieChart),
  { ssr: false, loading: CostsSkeleton },
);

// ---------------------------------------------------------------------------
// Mobile ordering
// ---------------------------------------------------------------------------
/**
 * The mobile reading order, and the only place it is defined.
 *
 * Desktop is a 2fr/1fr grid whose two columns each flow top-down, and the
 * mobile stack used to be the concatenation of those two columns — main column
 * first, sidebar after. Both columns lead with their most important card, so
 * everything in the sidebar landed below everything in the main column:
 * measured on a 390px viewport (Mercedes-Benz Group AG, guest), the financial
 * chart started at 2,438px and the Jahresabschlüsse — the cards users actually
 * pay credits for — at 4,082px of a 4,543px page. Effectively the bottom.
 *
 * The fix is CSS-only. Below `lg` both column wrappers are `display: contents`,
 * which erases their boxes and promotes every card to a direct child of the
 * single-column grid; the `order-*` values below then lay them out in an order
 * that owes nothing to the desktop columns. At `lg` the wrappers become flex
 * columns again and `lg:order-none` resets every card, so the desktop layout is
 * byte-for-byte what it was.
 *
 * Why not a `useIsMobile()` branch rendering two trees: the DOM order here
 * never changes, so the SSR HTML (which is what guests and crawlers get) is
 * untouched, there is no hydration mismatch, and heavy children — the XyFlow
 * network diagram above all — mount exactly once.
 *
 * Rules when editing:
 * - Tailwind only ships `order-1`..`order-12`; beyond that use the arbitrary
 *   form (`order-[13]`). Values must be written as literal strings so the JIT
 *   sees them — never build a class name at runtime.
 * - Every card gets `border-b border-border` from here rather than from a
 *   `[&>*]` rule on the wrapper: with `display: contents` the wrapper is not a
 *   box, so `[&>*:last-child]:border-b-0` would key off DOM position while the
 *   user sees `order` position.
 * - `min-w-0 overflow-hidden` likewise moves onto the cards. It used to sit on
 *   the left wrapper and is what stops a wide table (tenders, admin financials)
 *   from pushing the page sideways; `display: contents` would drop that guard.
 */
const CARD_BASE =
  'border-b border-border min-w-0 overflow-hidden lg:order-none';
const MOBILE_ORDER = {
  /** Identity, KPI row, About, NACE, network diagram, product/risk tags. */
  profile: `${CARD_BASE} order-1`,
  /** Revenue/profit/employees trend — the trajectory the KPI row can't show. */
  financialsChart: `${CARD_BASE} order-2`,
  /** Bilanz viewer & download. Monetised (credits) — kept high on purpose. */
  financialDocs: `${CARD_BASE} order-3`,
  /** AD/CD/DK register files. Monetised, and adjacent to the Bilanz card. */
  registerDocs: `${CARD_BASE} order-4`,
  /** Managers / Gesellschafter (gated) / Kommanditisten / Beteiligungen. */
  people: `${CARD_BASE} order-5`,
  /** Website, socials, email (gated). */
  contact: `${CARD_BASE} order-7`,
  /** Event timeline (blurred rows for guests). */
  history: `${CARD_BASE} order-8`,
  costs: `${CARD_BASE} order-9`,
  zeitverlauf: `${CARD_BASE} order-10`,
  /* The standalone tenders card used to hold order-11; tenders are now a tab
     on the history card, and funding took the freed slot. */
  funding: `${CARD_BASE} order-11`,
  /** Last: the only card that navigates away — the exit, not an off-ramp. */
  peers: `${CARD_BASE} order-12`,
  adminFinancials: `${CARD_BASE} order-[13]`,
  adminValidation: `${CARD_BASE} order-[14]`,
  adminActions: `${CARD_BASE} order-[15]`,
} as const;

/** Scroll targets for {@link MobileActionBar}; set as element ids on the cards. */
const FINANCIAL_DOCS_ANCHOR = 'jahresabschluesse';
const REGISTER_DOCS_ANCHOR = 'registerdateien';

type CompanyDetailPageProps = {
  euId: string;
  /** When provided (from SSR), the client skips the basic-info fetch entirely. */
  basicData?: CompanyBasicResponse | null;
  /** Pre-fetched network graph data from SSR — passed directly to the diagram. */
  networkData?: CompanyNetworkResponse | null;
};

/**
 * CompanyDetailPage — main company detail view using unified backend endpoints.
 *
 * This component makes only 5 API calls instead of 15+:
 * 1. /unified/basic - Fast initial load (SSR for guests)
 * 2. /unified/contact - Website & social (auto-backfills)
 * 3. /unified/register - Register + location + NACE (auto-backfills, SI+CD scraping)
 * 4. /unified/gesellschafter - Shareholders + DK file info (DK scraping)
 * 5. /unified/financials - Financial data + relationships + AI enrichments (auto-backfills)
 *
 * Guest users skip calls 2-5 and render from basicData only (no backfills triggered).
 * All backfill logic is handled by the backend - no conditional backfill logic needed here.
 *
 * ## CLS (Cumulative Layout Shift) Prevention Rules
 *
 * The SSR page must produce a stable layout for guests. Logged-in users get
 * progressive updates via client-side fetches, but the initial frame must not
 * jump. Follow these rules when adding or modifying sections:
 *
 * 1. **Parent conditions must match child thresholds.**
 *    If a chart component returns null when `data.length < N`, the parent
 *    conditional must use the SAME threshold. Otherwise a loading skeleton
 *    renders for the chunk, then the real component returns null → shift.
 *    Example: a chart that needs >= 50 values, so the parent checks >= 50.
 *
 * 2. **PermissionGatedContent must use `optimistic` on this page.**
 *    Without it, every gated section flashes a <Skeleton> while permissions
 *    resolve (session + guest-permission fetch). With `optimistic`, hidden
 *    gates render null during loading, and visible gates render children.
 *
 * 3. **Dynamic imports (`next/dynamic`) use `ssr: false` for recharts.**
 *    Recharts requires DOM measurement and can't SSR. The loading skeleton
 *    MUST match the expected rendered height. If the chart may return null
 *    based on data, tighten the parent condition (rule 1) so the skeleton
 *    never appears for cases that would produce null.
 *
 * 4. **Map containers need fixed height + background.**
 *    Leaflet is client-only. The `h-[200px] bg-muted/30` ensures the
 *    container occupies stable space before tiles load.
 *
 * 5. **Never conditionally render a section based on async-only data.**
 *    If data comes from a client fetch (contactData, financialsData), the
 *    section must either always render (with an empty/fallback state) or
 *    derive its condition from SSR-available basicData via effectiveXData.
 */
/**
 * Dev-only diagnostic logging.
 *
 * The Gesellschafterliste poll below fires up to 24 times per page view, so
 * in production it filled the console with two dozen lines per visit —
 * drowning anything a user or a support engineer actually needed to see.
 * Stripped entirely from production builds by the NODE_ENV check.
 */
const debugLog = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'development') console.info(...args);
};

export function CompanyDetailPage({
  euId,
  basicData: ssrBasicData,
  networkData: ssrNetworkData,
}: CompanyDetailPageProps) {
  const { data: session, status: sessionStatus } = useSession();
  const { t, i18n } = useTranslation();
  const recordCompanyView = useRecordCompanyView();
  const recordedCompanyViewsRef = useRef(new Set<string>());
  const fmt = useMemo(() => createFormatters(i18n.language), [i18n.language]);

  // ==========================================================================
  // STATE - one state per unified endpoint
  // ==========================================================================
  const [basicData, setBasicData] = useState<CompanyBasicResponse | null>(
    ssrBasicData ?? null,
  );
  const [contactData, setContactData] = useState<CompanyContactResponse | null>(
    null,
  );
  const [registerData, setRegisterData] =
    useState<CompanyRegisterResponse | null>(null);
  const [gesellschafterData, setGesellschafterData] =
    useState<CompanyGesellschafterResponse | null>(null);
  const [companyHistory, setCompanyHistory] = useState<CompanyHistory | null>(
    ssrBasicData?.company_history ?? null,
  );
  const [financialsData, setFinancialsData] =
    useState<CompanyFinancialsResponse | null>(null);
  const insolvencySummary =
    basicData?.insolvency ?? registerData?.insolvency ?? null;
  const insolvencyRecords = insolvencySummary?.records ?? [];
  // Resolved from the merged SI + CD + portal timeline. `company_status` is
  // the legacy binary flag and only ever says active/insolvent, so it could
  // not express a voluntary liquidation; `dissolved` alone could not tell a
  // company still being wound up from one already wound down.
  const legalStatus: CompanyLegalStatus =
    insolvencySummary?.status ??
    (insolvencySummary?.company_status === 'insolvent'
      ? 'insolvent'
      : insolvencySummary?.dissolved === true
        ? 'in_liquidation'
        : 'active');
  const [loading, setLoading] = useState(!ssrBasicData);
  const [loadingBackground, setLoadingBackground] = useState(false);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [loadingGesellschafter, setLoadingGesellschafter] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<Set<string>>(
    new Set(),
  );

  const isAdminUser = isAdmin(session);
  // Pro or Admin — gates the Bilanz PDF download in FinancialDocsCard.
  const isProOrAdmin = isAdminUser || isPro(session);

  // Wrapper setters that also update companyHistory with the latest value
  const handleSetBasicData = useCallback(
    (data: CompanyBasicResponse | null) => {
      setBasicData(data);
      if (data?.company_history) setCompanyHistory(data.company_history);
    },
    [],
  );
  const handleSetRegisterData = useCallback(
    (data: CompanyRegisterResponse | null) => {
      setRegisterData(data);
      if (data?.company_history) setCompanyHistory(data.company_history);
    },
    [],
  );

  // ==========================================================================
  // DATA FETCHING - 4 parallel calls, no backfill logic needed
  // ==========================================================================

  // Ref to prevent duplicate initial fetches in StrictMode
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    setError(null);

    const deniedSet = new Set<string>();

    // Helper to process a single response and update state immediately
    const processResponse = async <T,>(
      response: Response,
      key: string,
      setter: (data: T) => void,
    ): Promise<boolean> => {
      if (signal.aborted) return false;
      if (response.status === 403) {
        deniedSet.add(key);
        if (!signal.aborted) setPermissionDenied(new Set(deniedSet));
        return false;
      } else if (response.ok) {
        const json = await response.json();
        if (!signal.aborted) {
          setter(json.data);
        }
        return true;
      } else if (response.status === 429) {
        return false;
      }
      return false;
    };

    try {
      // Fire contact + register endpoints immediately (they run in parallel)
      // Each promise's .then() calls processResponse which updates state
      // immediately when data arrives — the UI re-renders progressively.
      const contactPromise = fetch(`/api/companies/${euId}/unified/contact`, {
        signal,
      })
        .then((res) => processResponse(res, 'contact', setContactData))
        .catch(() => false);

      setLoadingRegister(true);
      const registerPromise = fetch(`/api/companies/${euId}/unified/register`, {
        signal,
      })
        .then((res) => processResponse(res, 'register', handleSetRegisterData))
        .catch(() => false)
        .finally(() => {
          if (!signal.aborted) setLoadingRegister(false);
        });

      setLoadingGesellschafter(true);
      const gesellschafterPromise = fetch(
        `/api/companies/${euId}/unified/gesellschafter`,
        { signal },
      )
        .then((res) =>
          processResponse(res, 'gesellschafter', setGesellschafterData),
        )
        .catch(() => false)
        .finally(() => {
          if (!signal.aborted) setLoadingGesellschafter(false);
        });

      // Use SSR basic data if available, otherwise fetch from API
      let parsedBasicData: CompanyBasicResponse | null = ssrBasicData ?? null;
      if (!ssrBasicData) {
        const basicRes = await fetch(`/api/companies/${euId}/unified/basic`, {
          signal,
        });
        if (signal.aborted) return;

        if (basicRes.ok) {
          const json = await basicRes.json();
          parsedBasicData = json.data;
          if (!signal.aborted) {
            handleSetBasicData(parsedBasicData);
          }
        } else if (basicRes.status === 403) {
          deniedSet.add('basic');
        }
      }

      // Skip financials + AI enrichments for VR (Vereinsregister) companies
      const isVR = parsedBasicData?.register_type === 'VR';
      // The financials response is the only source of `has_file` on
      // financial_files — without it the Jahresabschluss buttons stay
      // disabled (basicData deliberately omits storage info). So it must
      // land fast: phase 1 is a cached read (refresh=false) that serves
      // already-stored files immediately. Phase 2 re-fetches with the
      // backend's on-demand Bundesanzeiger refresh (its default), which
      // downloads only publications not yet on file — worst case minutes —
      // and runs detached when phase 1 succeeded, silently swapping in any
      // newly found files. A transient 429/5xx/timeout on the refresh must
      // not strand the page on stale data until a manual reload, so it
      // retries with backoff; 403/404 are definitive and go straight to
      // processResponse.
      const refreshFinancialsWithRetry = async (): Promise<boolean> => {
        const retryDelaysMs = [2000, 5000];
        for (let attempt = 0; ; attempt++) {
          const isLastAttempt = attempt >= retryDelaysMs.length;
          try {
            const res = await fetch(
              `/api/companies/${euId}/unified/financials`,
              { signal },
            );
            if (
              res.ok ||
              res.status === 403 ||
              res.status === 404 ||
              isLastAttempt
            ) {
              return processResponse(res, 'financials', setFinancialsData);
            }
          } catch {
            if (signal.aborted || isLastAttempt) return false;
          }
          await new Promise((resolve) =>
            setTimeout(resolve, retryDelaysMs[attempt]),
          );
          if (signal.aborted) return false;
        }
      };
      const fetchFinancials = async (): Promise<boolean> => {
        try {
          const cachedRes = await fetch(
            `/api/companies/${euId}/unified/financials?refresh=false`,
            { signal },
          );
          const gotCached = await processResponse(
            cachedRes,
            'financials',
            setFinancialsData,
          );
          // 403/404 are definitive — no point firing the refresh pass.
          if (cachedRes.status === 403 || cachedRes.status === 404) {
            return gotCached;
          }
          if (gotCached) {
            // Detached on purpose: the page must not hold its background-
            // loading state hostage to a scrape that can take minutes.
            void refreshFinancialsWithRetry().catch(() => {});
            return true;
          }
        } catch {
          if (signal.aborted) return false;
        }
        // Cached read failed — fall back to the blocking refresh path.
        return refreshFinancialsWithRetry();
      };
      const financialsPromise = isVR
        ? Promise.resolve(false)
        : fetchFinancials();

      // Page can render with basic data — stop showing loading state immediately
      if (!signal.aborted) {
        setLoading(false);
        setPermissionDenied(new Set(deniedSet));
        setLoadingBackground(true);
      }

      // Wait for all fetches — each one already updates state via processResponse
      // as soon as it resolves, so the UI updates progressively
      await Promise.allSettled([
        contactPromise,
        registerPromise,
        gesellschafterPromise,
        financialsPromise,
      ]);

      if (!signal.aborted) {
        setLoadingBackground(false);
      }
    } catch (err) {
      if (!signal.aborted) {
        setError('Failed to load company data');
        setLoading(false);
      }
    }
  }, [euId, ssrBasicData]);

  // Initial fetch - only once per euId change
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [euId]);

  useEffect(() => {
    // Wait until session status is resolved
    if (sessionStatus === 'loading') return;

    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      if (session) {
        // Authenticated: fetch all endpoints (progressive loading with backfills)
        fetchData();
      } else {
        // Guest: skip all endpoint calls, basicData from SSR is sufficient
        setLoading(false);
      }
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, session, sessionStatus]);

  // Fire AI summary backfill once per euId, after financials lands and only
  // when the company has a balance sheet but no summary yet. The action is
  // idempotent (backend short-circuits "already_parsed"), and we refetch the
  // financials endpoint when it resolves so the UI fills in without a refresh.
  const aiSummaryFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session) return;
    if (!financialsData) return;
    if (aiSummaryFiredRef.current === euId) return;
    if (financialsData.ai_summary || financialsData.ai_summary_en) return;

    const hasBalanceSheet =
      (financialsData.aktiva_data?.length ?? 0) > 0 ||
      (financialsData.passiva_data?.length ?? 0) > 0;
    if (!hasBalanceSheet) return;

    aiSummaryFiredRef.current = euId;

    const controller = new AbortController();
    void backfillAiSummaryAction(euId)
      .then(async (res) => {
        if (controller.signal.aborted || !res.success) return;
        // Cached read: this refetch only wants the freshly parsed AI
        // summary fields — refresh=true would queue behind an in-flight
        // publications scrape for the same company.
        const refetch = await fetch(
          `/api/companies/${euId}/unified/financials?refresh=false`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted || !refetch.ok) return;
        const json = await refetch.json();
        if (!controller.signal.aborted) setFinancialsData(json.data);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [euId, session, financialsData]);

  // The /unified/gesellschafter call above may TRIGGER a backend parse of the
  // Gesellschafterliste (gesellschafter_backfill_triggered) whose result lands
  // a while later — so the network diagram rendered from SSR data lacks the
  // parsed shareholders. Poll the endpoint until the backend reports the parse
  // finished, then swap in the fresh gesellschafter data and bump the reload
  // token so CompanyNetworkDiagram refetches its graph.
  const [networkReloadToken, setNetworkReloadToken] = useState(0);
  useEffect(() => {
    if (!gesellschafterData) return;
    if (!gesellschafterData.gesellschafter_backfill_triggered) {
      debugLog(
        '[gesellschafter-poll] no parse in flight (backfill_triggered=false) — not polling',
        { euId, has_liste: gesellschafterData.has_gesellschafterliste },
      );
      return;
    }

    const POLL_MS = 5_000;
    const MAX_ATTEMPTS = 24; // give the parse ~2 minutes, then stop quietly
    let attempts = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    debugLog(
      `[gesellschafter-poll] parse triggered — polling every ${POLL_MS / 1000}s (max ${MAX_ATTEMPTS})`,
      { euId },
    );

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await fetch(
          `/api/companies/${euId}/unified/gesellschafter`,
        );
        if (!cancelled && res.ok) {
          const json = await res.json();
          const data = json.data as CompanyGesellschafterResponse | undefined;
          debugLog(
            `[gesellschafter-poll] attempt ${attempts}/${MAX_ATTEMPTS}`,
            {
              backfill_triggered: data?.gesellschafter_backfill_triggered,
              has_liste: data?.has_gesellschafterliste,
              gesellschafter: data?.gesellschafter.length,
            },
          );
          // Parse done: the backend no longer reports an in-flight backfill
          // and has parsed shareholders to show.
          if (
            data &&
            !data.gesellschafter_backfill_triggered &&
            (data.gesellschafter.length > 0 || data.has_gesellschafterliste)
          ) {
            debugLog(
              '[gesellschafter-poll] parse complete — reloading network diagram',
            );
            setGesellschafterData(data);
            setNetworkReloadToken((t) => t + 1);
            return;
          }
        }
      } catch {
        // transient network error — keep polling until the attempt cap
      }
      if (!cancelled && attempts < MAX_ATTEMPTS) {
        timer = setTimeout(poll, POLL_MS);
      } else if (!cancelled) {
        debugLog(
          '[gesellschafter-poll] gave up after max attempts — parse still not done',
        );
      }
    };

    timer = setTimeout(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [euId, gesellschafterData]);

  // Guest first-visit AI summary trigger. Guests skip fetchData() so we
  // can only gate on the SSR basicData (no financialsData available).
  // basicData.metrics is the closest proxy for "has financial data worth
  // summarising". The server action checks SystemLog for prior visits
  // by the same guestSub fingerprint and only fires once per IP+UA.
  useEffect(() => {
    if (sessionStatus === 'loading') return;
    if (session) return;
    if (!basicData) return;
    if (aiSummaryFiredRef.current === euId) return;
    if (basicData.ai_summary || basicData.ai_summary_en) return;

    const hasFinancialSignal = (basicData.metrics?.length ?? 0) > 0;
    if (!hasFinancialSignal) return;

    aiSummaryFiredRef.current = euId;

    const controller = new AbortController();
    void triggerAiSummaryForGuestIfFirstVisit(euId)
      .then((res) => {
        if (controller.signal.aborted) return;
        if (res.success && res.basicData) {
          handleSetBasicData(res.basicData);
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [euId, session, sessionStatus, basicData]);

  // Track company views in GA4.
  useEffect(() => {
    if (basicData?.eu_id) {
      trackCompanyView(
        basicData.eu_id,
        basicData.display_name || basicData.eu_id,
      );
    }
  }, [session, basicData?.eu_id, basicData?.display_name]);

  // Record each company once per mount, even when the session refetches.
  useEffect(() => {
    if (
      session?.user?.sub &&
      basicData?.eu_id &&
      !recordedCompanyViewsRef.current.has(basicData.eu_id)
    ) {
      recordedCompanyViewsRef.current.add(basicData.eu_id);
      const legalForm: unknown = [
        ...(basicData.company_history?.rechtsform ?? []),
      ].sort((a, b) =>
        (b.entry_date ?? '').localeCompare(a.entry_date ?? ''),
      )[0]?.data.rechtsform;
      recordCompanyView({
        euId: basicData.eu_id,
        companyName: basicData.display_name || basicData.eu_id,
        subtitle:
          [
            basicData.city || basicData.sitz,
            typeof legalForm === 'string' ? legalForm : null,
          ].filter(Boolean).join(' · ') || null,
        countryCode: companyCountryCode(basicData.register_type, basicData.eu_id),
        path: delocalizePath(window.location.pathname).pathname,
      });
    }
  }, [session?.user?.sub, basicData, recordCompanyView]);

  // ==========================================================================
  // EFFECTIVE DATA - Derive from basicData for guests, use fetched data for
  // authenticated users. This lets guest SSR render all visible sections.
  // ==========================================================================

  // WARNING: basicData.overrides comes from verified company owners and MUST
  // take highest priority over register, AI, and backfill data wherever they
  // overlap (address fields, about_us). Never let another source override these.
  const effectiveRegisterData = useMemo(
    () => getEffectiveCompanyRegisterData(basicData, registerData),
    [registerData, basicData],
  );

  // /gesellschafter answers after its page-load DK visit, so its catalog is
  // fresher when non-empty. Keep documents and completeness from that same source.
  const registerDocumentsSource = gesellschafterData?.register_documents?.length
    ? gesellschafterData
    : effectiveRegisterData;

  const effectiveFinancialsData = useMemo(
    () => getEffectiveCompanyFinancialsData(basicData, financialsData, euId),
    [financialsData, basicData, euId],
  );

  // ==========================================================================
  // COMPUTED DATA - Memoized to prevent unnecessary re-renders
  // ==========================================================================

  const hasGesellschafterliste =
    basicData?.has_gesellschafterliste ||
    gesellschafterData?.has_gesellschafterliste ||
    false;
  const gesellschafterItems = gesellschafterData?.gesellschafter ?? [];

  const heroData = useMemo(
    () => buildCompanyProfileHeroData(
      basicData, effectiveRegisterData, companyHistory, euId,
    ),
    [basicData, effectiveRegisterData, companyHistory, euId],
  );

  const statistics = useMemo(
    () => buildCompanyProfileStatistics(effectiveFinancialsData, t, fmt),
    [effectiveFinancialsData, t, fmt],
  );

  // Convert financial tables for existing components
  const aktivaData = useMemo<BalanceSheetFlatRow[] | null>(() => {
    return (
      effectiveFinancialsData?.aktiva_data?.map((row) => ({
        year: row.year,
        section: row.section ?? null,
        subsection: row.subsection ?? null,
        subsubsection: row.subsubsection ?? null,
        title: row.title,
        current_value: row.current_value ?? null,
        previous_value: row.previous_value ?? null,
      })) ?? null
    );
  }, [effectiveFinancialsData]);

  const passivaData = useMemo<BalanceSheetFlatRow[] | null>(() => {
    return (
      effectiveFinancialsData?.passiva_data?.map((row) => ({
        year: row.year,
        section: row.section ?? null,
        subsection: row.subsection ?? null,
        subsubsection: row.subsubsection ?? null,
        title: row.title,
        current_value: row.current_value ?? null,
        previous_value: row.previous_value ?? null,
      })) ?? null
    );
  }, [effectiveFinancialsData]);

  const guvData = useMemo<GuvFlatRow[] | null>(() => {
    return (
      effectiveFinancialsData?.guv_data?.map((row) => ({
        year: row.year,
        section: row.section ?? null,
        subsection: row.subsection ?? null,
        subsubsection: row.subsubsection ?? null,
        subsubsubsection: row.subsubsubsection ?? null,
        title: row.title,
        current_value: row.current_value ?? null,
        previous_value: row.previous_value ?? null,
      })) ?? null
    );
  }, [effectiveFinancialsData]);

  const aiGuvData = useMemo<GuvFlatRow[] | null>(() => {
    return (
      effectiveFinancialsData?.ai_guv_data?.map((row) => ({
        year: row.year,
        section: row.section ?? null,
        subsection: row.subsection ?? null,
        subsubsection: row.subsubsection ?? null,
        subsubsubsection: row.subsubsubsection ?? null,
        title: row.title,
        current_value: row.current_value ?? null,
        previous_value: row.previous_value ?? null,
      })) ?? null
    );
  }, [effectiveFinancialsData]);

  const mergedGuvData = useMemo(() => {
    return [...(guvData || []), ...(aiGuvData || [])];
  }, [guvData, aiGuvData]);

  // Mirror CompanyCostsPieChart's data condition so the chart/table toggle is
  // only rendered when at least one year has recognizable cost categories —
  // otherwise both the pie and the table return null and we'd show an empty
  // toggle bar.
  const hasCostBreakdown = useMemo(() => {
    if (mergedGuvData.length === 0) return false;
    const categories = isUkvStyle(mergedGuvData)
      ? getUkvCategories(t)
      : getGkvCategories(t);
    const years = new Set(mergedGuvData.map((d) => d.year));
    for (const year of years) {
      const yearData = mergedGuvData.filter((d) => d.year === year);
      const sum = extractCostsForYear(yearData, categories).reduce(
        (s, c) => s + c.value,
        0,
      );
      if (sum > 0) return true;
    }
    return false;
  }, [mergedGuvData, t]);

  // Unique years present in financial data (for admin correction form)
  const availableFinancialYears = useMemo(() => {
    const years = new Set<number>();
    effectiveFinancialsData?.metrics?.forEach((m) => {
      if (typeof m.year === 'number') years.add(m.year);
    });
    const collectYears = (rows: { year?: number }[] | undefined | null) => {
      rows?.forEach((r) => {
        if (typeof r.year === 'number') years.add(r.year);
      });
    };
    collectYears(effectiveFinancialsData?.aktiva_data);
    collectYears(effectiveFinancialsData?.passiva_data);
    collectYears(effectiveFinancialsData?.guv_data);
    collectYears(effectiveFinancialsData?.ai_guv_data);
    return Array.from(years);
  }, [effectiveFinancialsData]);

  // Revenue history for trend chart
  const revenueHistory = useMemo(() => {
    const employeeByYear = new Map(
      effectiveFinancialsData?.employee_data?.map((e) => [
        e.year,
        e.total ?? null,
      ]) ?? [],
    );
    return (
      effectiveFinancialsData?.metrics?.map((m) => ({
        year: m.year,
        totalRevenue: m.total_revenue ?? null,
        profit: m.profit ?? null,
        totalEmployees: employeeByYear.get(m.year) ?? null,
        totalBalanceSum: m.total_balance_sum ?? m.passiva ?? null,
        grossProfit: m.gross_profit ?? null,
        otherOperatingIncome: m.other_operating_income ?? null,
        sizeClass: m.size_class ?? null,
        revenueCeiling: m.revenue_ceiling ?? null,
        sizeBasisCode: m.size_basis_code ?? null,
        ceilingBasisCode: m.ceiling_basis_code ?? null,
      })) ?? []
    );
  }, [effectiveFinancialsData]);

  // Mirrors FinancialDocsCard's own filter — the mobile action bar must not
  // offer a "Bilanz" jump to a card that hides itself for lack of downloadable
  // file types.
  const hasFinancialDocs = useMemo(
    () =>
      (effectiveFinancialsData?.financial_files ?? []).some((f) =>
        DOWNLOADABLE_FILE_TYPES.includes(f.file_type),
      ),
    [effectiveFinancialsData],
  );

  // Validation errors for admin display
  const validationErrors = useMemo(() => {
    const logs = effectiveFinancialsData?.validation_logs ?? [];
    const yearGroups = new Map<
      number,
      {
        errorType: string;
        severity: string;
        message: string;
        details: unknown;
      }[]
    >();

    for (const log of logs) {
      const year = log.year ?? 0;
      if (!yearGroups.has(year)) yearGroups.set(year, []);

      let message = 'Validation error';
      if (log.details && typeof log.details === 'object') {
        const parts: string[] = [];
        if (log.details.ratio !== undefined)
          parts.push(`ratio: ${log.details.ratio}`);
        if (log.details.current_val !== undefined)
          parts.push(`current: ${log.details.current_val}`);
        if (log.details.previous_val !== undefined)
          parts.push(`previous: ${log.details.previous_val}`);
        if (parts.length > 0) message += ` (${parts.join(', ')})`;
      }

      yearGroups.get(year)!.push({
        errorType: log.error_type ?? 'Unknown',
        severity: log.severity ?? 'ERROR',
        message,
        details: log.details,
      });
    }

    const entries = Array.from(yearGroups.entries())
      .sort(([a], [b]) => b - a)
      .map(([year, errors]) => ({
        year: year || null,
        errorCount: errors.length,
        errors,
      }));

    return {
      total: entries.reduce((sum, e) => sum + e.errorCount, 0),
      entries,
    };
  }, [effectiveFinancialsData]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  if (loading) {
    return (
      <div id="company-detail-print" className="bg-background">
        <Container className="flex flex-col gap-6 py-8 lg:gap-7.5">
          <div className="text-center text-muted-foreground">
            Loading company data...
          </div>
        </Container>
      </div>
    );
  }

  if (!basicData && !permissionDenied.has('basic')) {
    return null;
  }

  const showRegisterAttribution =
    !!basicData && basicData.register_type !== 'CH-HR';
  const attributionRegisterId = formatRegisterNumber(
    basicData?.register_type,
    basicData?.register_id,
  );

  return (
    <div id="company-detail-print" className="bg-background">
      {/* Freeze forensics for the sporadic detail-page lockups: long-task
          observer + hard-freeze watchdog beaconing to /api/log/client-freeze.
          Company + network size in context so frozen sessions are attributable
          to a specific company's data shape. */}
      <FreezeReporter
        context={{
          page: 'company-detail',
          euId,
          networkNodes: ssrNetworkData?.nodes.length ?? 0,
          networkEdges: ssrNetworkData?.edges.length ?? 0,
        }}
      />
      {/* pb-24 below lg keeps the last card clear of the fixed action bar. */}
      <Container className="flex flex-col pt-0 pb-24 lg:pb-8 px-0 lg:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] overflow-hidden border-b border-border">
          {/* Left Column - Top (Profile, Managers, Register, Subsidiaries).
              `contents` below lg dissolves this wrapper so its cards become
              direct grid items and MOBILE_ORDER can interleave them with the
              sidebar's; `lg:flex` restores the desktop column. */}
          <div className="contents lg:flex lg:flex-col lg:order-1 min-w-0 lg:overflow-hidden">
            {basicData && (
              <CompanyProfileSection
                className={MOBILE_ORDER.profile}
                basicData={basicData}
                registerData={effectiveRegisterData}
                financialsData={effectiveFinancialsData}
                contactData={contactData}
                heroData={heroData}
                statistics={statistics}
                isLoadingBackground={loadingBackground}
                euId={euId}
                networkData={ssrNetworkData}
                networkReloadToken={networkReloadToken}
                insolvencyRecords={insolvencyRecords}
                legalStatus={legalStatus}
                isDeleted={heroData.isDeleted}
              />
            )}

            {!heroData.isDeleted &&
              (effectiveRegisterData?.participants?.length ||
                hasGesellschafterliste ||
                (effectiveFinancialsData?.subsidiaries?.length ?? 0) > 0) && (
                <PeopleTabbedCard
                  className={MOBILE_ORDER.people}
                  participants={effectiveRegisterData?.participants ?? []}
                  gesellschafter={gesellschafterItems}
                  hasGesellschafterliste={hasGesellschafterliste}
                  kommanditisten={companyHistory?.kommanditisten ?? []}
                  // Only show Kommanditanteil % for GmbH/UG/AG & Co. KG. In a
                  // standalone KG the Komplementär also holds ownership we can't
                  // see from the CD, so the Kommanditisten render as owners
                  // without a (misleading) percentage.
                  showKommanditPercent={/[&+]\s*Co\./i.test(
                    basicData?.display_name ?? '',
                  )}
                  subsidiaries={effectiveFinancialsData?.subsidiaries ?? []}
                  isAuthenticated={!!session}
                  euId={euId}
                  showRegisterAttribution={showRegisterAttribution}
                  registerCourt={basicData?.register_court}
                  registerId={attributionRegisterId}
                  capturedAt={basicData?.updated_at}
                />
              )}


            {/* Tenders live here as a tab rather than in a card of their
                own. The mount guard has to admit tenders separately:
                `companyHistory` is null without a CD event aggregate, and
                38% of the companies that have tenders have no CD row — under
                a `companyHistory &&` guard alone their Vergabe tab would
                never render. Tender data rides along on the SSR basic
                payload, so there is no second fetch and no layout shift. */}
            {(companyHistory || (basicData?.tenders?.length ?? 0) > 0) && (
              <CompanyHistoryCard
                className={MOBILE_ORDER.history}
                history={companyHistory ?? null}
                isAuthenticated={!!session}
                subsidiaries={effectiveFinancialsData?.subsidiaries ?? []}
                insolvencyRecords={insolvencyRecords}
                tenders={basicData?.tenders ?? []}
                cpvProducts={basicData?.cpv_products ?? []}
                tendersTotal={basicData?.tenders_total ?? 0}
                euId={euId}
                showRegisterAttribution={showRegisterAttribution}
                registerCourt={basicData?.register_court}
                registerId={attributionRegisterId}
                capturedAt={basicData?.updated_at}
              />
            )}

            {/* Admin-only detailed financial tables */}
            {isAdminUser &&
              !!(
                aktivaData?.length ||
                passivaData?.length ||
                guvData?.length ||
                aiGuvData?.length
              ) && (
                <CollapsibleCard
                  className={MOBILE_ORDER.adminFinancials}
                  title={t(
                    'companyDetail.detailedFinancialData',
                    'Detailed Financial Data',
                  )}
                  defaultOpen={false}
                >
                  <div className="p-4 space-y-4">
                    {aktivaData && aktivaData.length > 0 && (
                      <FlatAktivaFinancialTable
                        data={aktivaData}
                        title="AKTIVA Data"
                        defaultOpen={false}
                      />
                    )}
                    {passivaData && passivaData.length > 0 && (
                      <FlatPassivaFinancialTable
                        data={passivaData}
                        title="PASSIVA Data"
                        defaultOpen={false}
                      />
                    )}
                    {guvData && guvData.length > 0 && (
                      <FlatFinancialTable
                        data={guvData as FlatFinancialRow[]}
                        title="GUV Data"
                        defaultOpen={false}
                      />
                    )}
                    {aiGuvData && aiGuvData.length > 0 && (
                      <FlatFinancialTable
                        data={aiGuvData as FlatFinancialRow[]}
                        title="AI GUV Data"
                        defaultOpen={false}
                      />
                    )}
                    <CollapsibleCard
                      title="Raw Financial Data"
                      defaultOpen={false}
                    >
                      <div className="p-0 overflow-auto">
                        <RawFinancialsCard
                          euId={euId}
                          className="rounded-none border-0 shadow-none"
                          showHeader={false}
                          density="compact"
                        />
                      </div>
                    </CollapsibleCard>
                  </div>
                </CollapsibleCard>
              )}

            {/* Admin-only validation errors */}
            {isAdminUser && validationErrors.total > 0 && (
              <CompanyValidationErrorsCard
                className={MOBILE_ORDER.adminValidation}
                validationErrors={validationErrors}
              />
            )}

            {/* Admin actions */}
            {isAdminUser && (
              <CompanyAdminActionsCard
                className={MOBILE_ORDER.adminActions}
                euId={euId}
                availableYears={availableFinancialYears}
              />
            )}
          </div>

          {/* Right Column - Sidebar (Financials, Register, Kontakt, Costs).
              Same `contents` trick as the left column — see MOBILE_ORDER. */}
          <div className="contents lg:flex lg:flex-col lg:order-2 lg:border-l lg:border-border min-w-0 lg:[&>*]:shrink-0">
            {revenueHistory.some(
              (e) =>
                e.totalRevenue != null ||
                e.totalBalanceSum != null ||
                e.profit != null ||
                e.totalEmployees != null,
            ) && (
              <ChartTableTabs
                className={MOBILE_ORDER.financialsChart}
                chart={
                  <CompanyTrendChart
                    revenueHistory={revenueHistory}
                    guvData={mergedGuvData}
                  />
                }
                table={
                  <FinancialHistoryTable
                    revenueHistory={revenueHistory}
                    companyName={heroData.name}
                  />
                }
              />
            )}

            {basicData?.register_type !== 'CH-HR' && (
              <RegisterDocsCard
                className={MOBILE_ORDER.registerDocs}
                id={REGISTER_DOCS_ANCHOR}
                registerFiles={effectiveRegisterData?.register_files ?? []}
                registerDocuments={registerDocumentsSource?.register_documents}
                registerDocumentsComplete={
                  registerDocumentsSource?.register_documents_complete
                }
                dkRegisterFile={
                  gesellschafterData?.dk_register_file ??
                  basicData?.register_files?.find(
                    (f) => f.file_type === 'HandelsRegisterDKGesellschafter',
                  ) ??
                  null
                }
                hasGesellschafterliste={hasGesellschafterliste}
                rechtsformCode={
                  effectiveRegisterData?.rechtsform_code ??
                  basicData?.rechtsform_code
                }
                gruendungsdokument={
                  effectiveRegisterData?.gruendungsdokument ??
                  basicData?.gruendungsdokument
                }
                ersteEintragungDatum={
                  effectiveRegisterData?.erste_eintragung_datum ??
                  basicData?.erste_eintragung_datum
                }
                euId={euId}
                isAuthenticated={!!session}
                isRegisterLoading={loadingRegister && !!session}
                isGesellschafterLoading={loadingGesellschafter && !!session}
                isDeleted={heroData.isDeleted}
                showRegisterAttribution={showRegisterAttribution}
                registerCourt={basicData?.register_court}
                registerId={attributionRegisterId}
                capturedAt={basicData?.updated_at}
              />
            )}

            {/* Contact info: website + social visible to all, emails gated */}
            <ContactInformationCard
              className={MOBILE_ORDER.contact}
              contact={contactData}
              basicData={basicData}
              showEmails={!!session}
              euId={euId}
            />

            <CompanyPeerSearchCard
              className={MOBILE_ORDER.peers}
              euId={basicData?.eu_id ?? euId}
              revenue={effectiveFinancialsData?.metrics?.[0]?.total_revenue}
              balanceSum={
                effectiveFinancialsData?.metrics?.[0]?.total_balance_sum ??
                effectiveFinancialsData?.metrics?.[0]?.passiva
              }
              naceCodes={
                effectiveRegisterData?.nace_codes ?? basicData?.nace_codes ?? []
              }
              similarCompanies={basicData?.similar_companies ?? []}
            />

            {hasCostBreakdown && (
              <ChartTableTabs
                className={MOBILE_ORDER.costs}
                chart={
                  <CompanyCostsPieChart
                    guvData={mergedGuvData}
                    revenueHistory={revenueHistory}
                  />
                }
                table={<CostsBreakdownTable guvData={mergedGuvData} />}
              />
            )}

            <ZeitverlaufTabbedCard
              className={MOBILE_ORDER.zeitverlauf}
              mergedGuvData={mergedGuvData}
              revenueHistory={revenueHistory}
              isAuthenticated={!!session}
              euId={euId}
            />

            {session &&
              contactData?.enrichment &&
              (contactData.enrichment.funding_last_round_amount != null ||
                contactData.enrichment.funding_rounds != null ||
                contactData.enrichment.funding_investor_count != null ||
                contactData.enrichment.funding_last_round_type != null) && (
                <FundingCard
                  className={MOBILE_ORDER.funding}
                  linkedin={contactData.enrichment}
                  language={i18n.language}
                />
              )}
            {!session && basicData?.has_funding_data && (
              <FundingCardPlaceholder
                className={MOBILE_ORDER.funding}
                euId={euId}
              />
            )}
            <FinancialDocsCard
              className={MOBILE_ORDER.financialDocs}
              id={FINANCIAL_DOCS_ANCHOR}
              financialFiles={effectiveFinancialsData?.financial_files ?? []}
              euId={euId}
              isAuthenticated={!!session}
              canDownload={isProOrAdmin}
            />
            {/* Empty spacer to fill remaining height. Desktop only — below lg
                the wrapper is `display: contents`, so it would otherwise sit in
                the mobile stack as a grid row of its own. */}
            <div className="hidden lg:block grow" />
          </div>
        </div>
        {/* Herkunft + Veränderungshinweis for Swiss register data — a reuse
            condition of the Swiss registry. */}
        {basicData?.register_type === 'CH-HR' && <SwissDataSourceNote />}
      </Container>
      {/* German register documents are always offered; Swiss companies have no
          Handelsregister documents, so the mobile action hides the anchor. */}
      <MobileActionBar
        euId={basicData?.eu_id ?? euId}
        isAuthenticated={!!session}
        financialDocsAnchor={FINANCIAL_DOCS_ANCHOR}
        registerDocsAnchor={REGISTER_DOCS_ANCHOR}
        hasFinancialDocs={hasFinancialDocs}
        hasRegisterDocs={basicData?.register_type !== 'CH-HR'}
      />
    </div>
  );
}

export default CompanyDetailPage;
